import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { registerNetworkMonitor, unregisterNetworkMonitor } from '../utils/api.js';

const NetworkMonitorContext = createContext();

export const NetworkMonitorProvider = ({ children }) => {
  const location = useLocation();
  const [requests, setRequests] = useState([]);
  const [maxRequests] = useState(100); // 최대 저장 개수
  const [isEnabled, setIsEnabled] = useState(true); // 항상 활성화
  const [isConsoleVisible, setIsConsoleVisible] = useState(false);
  const previousPathRef = useRef(location.pathname);

  // 네트워크 모니터링 콜백
  const handleNetworkEvent = useCallback((eventType, data) => {
    if (!isEnabled) return;

    if (eventType === 'request') {
      // 새 요청 추가
      setRequests(prev => {
        const newRequests = [data, ...prev];
        return newRequests.slice(0, maxRequests);
      });
    } else if (eventType === 'response' || eventType === 'error') {
      // 요청 업데이트
      setRequests(prev => 
        prev.map(req => 
          req.id === data.id 
            ? { ...req, ...data }
            : req
        )
      );
    }
  }, [isEnabled, maxRequests]);

  // 모니터링 활성화/비활성화 시 콜백 등록/해제
  useEffect(() => {
    if (isEnabled) {
      registerNetworkMonitor(handleNetworkEvent);
    } else {
      unregisterNetworkMonitor();
    }
    
    return () => {
      unregisterNetworkMonitor();
    };
  }, [isEnabled, handleNetworkEvent]);

  // URL 변경 감지하여 요청 목록 초기화
  useEffect(() => {
    const currentPath = location.pathname;
    const previousPath = previousPathRef.current;
    
    // 루트 경로가 변경된 경우에만 초기화 (상세 페이지 이동 등은 제외)
    const currentRoot = currentPath.split('/')[1];
    const previousRoot = previousPath.split('/')[1];
    
    if (currentRoot !== previousRoot) {
      setRequests([]);
    }
    
    previousPathRef.current = currentPath;
  }, [location.pathname]);

  // 요청 추가 (하위 호환성)
  const addRequest = useCallback((requestData) => {
    if (!isEnabled) return;
    handleNetworkEvent('request', requestData);
  }, [isEnabled, handleNetworkEvent]);

  // 요청 업데이트 (하위 호환성)
  const updateRequest = useCallback((id, responseData) => {
    if (!isEnabled) return;
    handleNetworkEvent('response', { id, ...responseData });
  }, [isEnabled, handleNetworkEvent]);

  // 요청 목록 초기화
  const clearRequests = useCallback(() => {
    setRequests([]);
  }, []);

  // 모니터링 활성화/비활성화
  const toggleMonitoring = useCallback(() => {
    setIsEnabled(prev => !prev);
  }, []);

  // 콘솔 표시/숨김 토글
  const toggleConsole = useCallback(() => {
    setIsConsoleVisible(prev => !prev);
  }, []);

  // 실패한 요청 카운트
  const errorCount = requests.filter(req => 
    req.status === 'error' || (req.statusCode && req.statusCode >= 400)
  ).length;

  const value = {
    requests,
    isEnabled,
    isConsoleVisible,
    errorCount,
    addRequest,
    updateRequest,
    clearRequests,
    toggleMonitoring,
    toggleConsole,
  };

  return (
    <NetworkMonitorContext.Provider value={value}>
      {children}
    </NetworkMonitorContext.Provider>
  );
};

export const useNetworkMonitor = () => {
  const context = useContext(NetworkMonitorContext);
  if (!context) {
    throw new Error('useNetworkMonitor must be used within NetworkMonitorProvider');
  }
  return context;
};

